import { observer } from 'mobx-react';
import React, { Component } from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import './style.scss';

import Icon from 'components/Icon/Icon';
import Spinner from 'components/Spinner/Spinner';

const MAX_FILE_SIZE = 2.5 * 1024 * 1024;

interface Props {
  onFileChange?: (file: File) => void;
  className?: string;
  onRemove?: () => void;
  file: File | null;
  fileUrl?: string;
  accept?: string | string[];
  maxSize?: number;
}

@observer
class FileUploader extends Component<Props> {
  onDrop = (files: File[]) => {
    if (!files.length) {
      return;
    }
    const file = files[0];

    this.props.onFileChange?.(file);
  };

  render() {
    const { file, fileUrl } = this.props;
    const maxSize = this.props.maxSize || MAX_FILE_SIZE;

    return (
      <Dropzone
        onDrop={this.onDrop}
        accept={this.props.accept || ['.jpg', '.jpeg', '.png']}
        multiple={false}
        maxSize={maxSize}
      >
        {({ getRootProps, getInputProps, isDragActive, rejectedFiles }) => {
          const isFileSizeError =
            !!rejectedFiles.length && rejectedFiles[0].size > maxSize;
          return (
            <div
              {...getRootProps()}
              className={`fileUploader__wrapper ${isDragActive &&
                'fileUploader__wrapper_active'} ${(!!file || !!fileUrl) &&
                'fileUploader__wrapper_uploaded'} ${!file &&
                !!fileUrl &&
                'fileUploader__wrapper_noBorder'}`}
            >
              <input {...getInputProps()} />

              <div
                className={`fileUploader__info ${(!!file || !!fileUrl) &&
                  'fileUploader__info_uploaded'} ${!file &&
                  !!fileUrl &&
                  'fileUploader__info_fromServer'}`}
              >
                <div
                  className={`fileUploader__preview ${isDragActive &&
                    'fileUploader__preview_loading'}`}
                >
                  {!!file || !!fileUrl ? (
                    <img
                      src={file ? URL.createObjectURL(file) : fileUrl}
                      alt={'dish preview'}
                      className={'fileUploader__preview__image'}
                    />
                  ) : isDragActive ? (
                    <Spinner
                      className={{
                        wrap: 'fileUploader__spinner_wrap',
                        spinner: 'fileUploader__spinner',
                        inner: 'fileUploader__spinner_inner',
                      }}
                    />
                  ) : (
                    <Icon
                      type="imageEmpty"
                      className={'fileUploader__noImage'}
                    />
                  )}
                </div>

                <div className={'fileUploader__description'}>
                  {!!file || !!fileUrl ? (
                    this.renderFilePreview(file)
                  ) : (
                    <>
                      <div className={`fileUploader__description__button`}>
                        <FormattedMessage
                          id="fileUploader.description"
                          values={{
                            browse: (
                              <span className={'fileUploader__button'}>
                                <FormattedMessage id="fileUploader.browse" />
                              </span>
                            ),
                          }}
                        />
                      </div>
                      <div
                        className={`fileUploader__limit ${isFileSizeError &&
                          'fileUploader__error'}`}
                      >
                        <FormattedMessage
                          id={
                            isFileSizeError
                              ? 'fileUploader.sizeError'
                              : 'fileUploader.limit'
                          }
                          values={{ value: maxSize / 1024 / 1024 }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        }}
      </Dropzone>
    );
  }

  renderFilePreview(file: File | null) {
    const onRemoveFile = e => {
      e.preventDefault();
      e.stopPropagation();

      this.props.onRemove?.();
    };

    return (
      <div className={'fileLoader__files_uploaded'}>
        <div>
          {file?.name && (
            <>
              <span className="fileLoader__image_title">
                <FormattedMessage
                  id="fileUploader.image.title"
                  values={{
                    fileName: file?.name,
                  }}
                />
              </span>
              <p className={'fileUploader__description__image_size'}>
                {(file?.size / 1024).toFixed(2)} kb
              </p>
            </>
          )}
        </div>
        <button
          onClick={onRemoveFile}
          className={'fileUploader__button_remove'}
        >
          <Icon type={'close'} />
        </button>
      </div>
    );
  }
}

export default FileUploader;
